/**
 * Publishes two variables on <html>:
 *
 *   --header-visible-height               how many pixels of the announcement bar
 *                                         (.announcement-bar-section) and the header
 *                                         (.header-wrapper) are on screen, summed
 *   --header-visible-height-without-menu  only the announcement bar plus the USP
 *                                         strip (.header__usp, inside .header-wrapper)
 *                                         — the logo and nav rows are left out
 *
 * Each element contributes only the part of it that overlaps the viewport, so
 * the values follow every state without knowing which classes the sticky
 * header applies — full height at the top of the page, shrinking as the bar and
 * header scroll away, the header's height again when it re-reveals, and 0 while
 * it is translated out of view. Sticky elements offset by
 * calc(<gap> + var(--header-visible-height)) and never sit underneath it.
 */
(() => {
  const SELECTORS = ['.announcement-bar-section', '.header-wrapper'];
  const USP_SELECTOR = '.header__usp';
  const root = document.documentElement;

  let elements = [];
  let usp = null;
  let queued = false;
  let last = -1;
  let lastWithoutMenu = -1;

  const visibleHeight = (element) => {
    const rect = element.getBoundingClientRect();
    return Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
  };

  const measure = () => {
    queued = false;
    const bar = elements[0].matches('.announcement-bar-section') ? visibleHeight(elements[0]) : 0;
    const total = Math.round(elements.reduce((sum, element) => sum + visibleHeight(element), 0));
    const withoutMenu = Math.round(bar + (usp ? visibleHeight(usp) : 0));

    if (total !== last) {
      last = total;
      root.style.setProperty('--header-visible-height', `${total}px`);
    }
    if (withoutMenu !== lastWithoutMenu) {
      lastWithoutMenu = withoutMenu;
      root.style.setProperty('--header-visible-height-without-menu', `${withoutMenu}px`);
    }
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  };

  const init = () => {
    elements = SELECTORS.map((selector) => document.querySelector(selector)).filter(Boolean);
    if (!elements.length) return;
    usp = document.querySelector(USP_SELECTOR);

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    // The sticky header's reveal/hide animation moves it after the scroll event
    // settles, and the bar/header can change height without a window resize
    // (announcement slides, fonts loading, the mobile menu).
    const header = document.querySelector('.section-header') || elements[elements.length - 1];
    header.addEventListener('transitionend', schedule);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      elements.forEach((element) => observer.observe(element));
      if (usp) observer.observe(usp);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
